import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, fmtINR, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Paperclip, Check } from "@phosphor-icons/react";

const ADMIN_ROLES = new Set(["admin", "director"]);
const FINANCE_ROLES = new Set(["admin", "director", "finance", "accounts"]);
const ALLOWED = new Set([...ADMIN_ROLES, ...FINANCE_ROLES, "centre_coordinator"]);

export default function ProfitShare() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [centres, setCentres] = useState([]);
  const [users, setUsers] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [openConfig, setOpenConfig] = useState(false);
  const [cf, setCf] = useState({ centre_id: "", coordinator_user_id: "", share_pct: 50, effective_from: new Date().toISOString().slice(0, 10) });
  const [payingId, setPayingId] = useState(null);
  const [pf, setPf] = useState({ payment_mode: "NEFT", payment_date: new Date().toISOString().slice(0, 10), utr: "", ack_file_id: "", ack_filename: "", remarks: "" });

  const canAdmin = ADMIN_ROLES.has(user?.role);
  const canPay = FINANCE_ROLES.has(user?.role);

  const load = () => {
    api.get("/profit-share/dashboard").then((r) => setRows(r.data));
    api.get("/profit-share/ledger").then((r) => setLedger(r.data));
    if (canAdmin) {
      api.get("/profit-share/configs").then((r) => setConfigs(r.data));
      api.get("/centres").then((r) => setCentres(r.data));
      api.get("/users").then((r) => setUsers(r.data.filter((u) => u.role === "centre_coordinator" && u.is_active !== false)));
    }
  };
  useEffect(() => { if (user && ALLOWED.has(user.role)) load(); /* eslint-disable-next-line */ }, [user]);
  if (user && !ALLOWED.has(user.role)) return <Navigate to="/dashboard" replace />;

  const saveConfig = async () => {
    if (!cf.centre_id || !cf.coordinator_user_id) return toast.error("Centre and coordinator required");
    if (!(cf.share_pct >= 0 && cf.share_pct <= 100)) return toast.error("Share % must be 0-100");
    try {
      await api.post("/profit-share/configs", cf);
      toast.success("Profit-share configuration saved");
      setOpenConfig(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const uploadAck = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setPf((p) => ({ ...p, ack_file_id: r.data.id, ack_filename: r.data.original_filename }));
    toast.success(`Attached ${file.name}`);
  };

  const pay = async (row) => {
    if (!pf.ack_file_id) return toast.error("Payment acknowledgement upload is mandatory.");
    try {
      await api.post(`/profit-share/${row.config_id}/pay`, pf);
      toast.success(`Paid ${row.coordinator.name} — ₹${row.outstanding.toLocaleString("en-IN")}`);
      setPayingId(null);
      setPf({ payment_mode: "NEFT", payment_date: new Date().toISOString().slice(0, 10), utr: "", ack_file_id: "", ack_filename: "", remarks: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="profit-share-root">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Finance · Coordinator Profit Share</div>
          <h1 className="font-heading text-3xl mt-1">Centre Coordinator Profit Share</h1>
          <p className="text-sm text-zinc-600 mt-1">Live-computed shares based on approved income − approved expenses − approved assets, per centre.</p>
        </div>
        {canAdmin && (
          <Dialog open={openConfig} onOpenChange={setOpenConfig}>
            <DialogTrigger asChild>
              <Button data-testid="ps-new-config-btn" className="rounded-sm bg-zinc-900 text-white gap-2"><Plus size={14} /> New Configuration</Button>
            </DialogTrigger>
            <DialogContent className="max-w-md bg-white">
              <DialogHeader><DialogTitle className="font-heading">New profit-share config</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Centre</Label>
                  <Select value={cf.centre_id} onValueChange={(v) => setCf({ ...cf, centre_id: v })}>
                    <SelectTrigger className="rounded-sm h-10 mt-1" data-testid="ps-config-centre"><SelectValue placeholder="Select centre" /></SelectTrigger>
                    <SelectContent>{centres.map(c => <SelectItem key={c.id} value={c.id}>{c.code} · {c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Coordinator</Label>
                  <Select value={cf.coordinator_user_id} onValueChange={(v) => setCf({ ...cf, coordinator_user_id: v })}>
                    <SelectTrigger className="rounded-sm h-10 mt-1" data-testid="ps-config-coord"><SelectValue placeholder="Select coordinator" /></SelectTrigger>
                    <SelectContent>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.name} · {u.email}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Share %</Label>
                  <Input type="number" step="0.01" min="0" max="100" value={cf.share_pct}
                    onChange={(e) => setCf({ ...cf, share_pct: parseFloat(e.target.value) || 0 })}
                    className="rounded-sm h-10 mt-1 tabular-nums" data-testid="ps-config-pct" />
                </div>
                <div><Label>Effective From</Label>
                  <Input type="date" value={cf.effective_from} onChange={(e) => setCf({ ...cf, effective_from: e.target.value })} className="rounded-sm h-10 mt-1" />
                </div>
                <Button onClick={saveConfig} data-testid="ps-config-save" className="w-full bg-emerald-700 hover:bg-emerald-800 text-white">Save configuration</Button>
                <p className="text-xs text-zinc-500">Any existing active config for the same centre will be superseded — history preserved.</p>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Live dashboard */}
      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[10px] uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="text-left p-3">Centre</th>
              <th className="text-left p-3">Coordinator</th>
              <th className="text-right p-3">Income</th>
              <th className="text-right p-3">Expenses</th>
              <th className="text-right p-3">Assets</th>
              <th className="text-right p-3">Net Profit</th>
              <th className="text-right p-3">%</th>
              <th className="text-right p-3">Coord Share</th>
              <th className="text-right p-3">Company</th>
              <th className="text-right p-3">Paid</th>
              <th className="text-right p-3">Outstanding</th>
              <th className="text-right p-3">Action</th>
            </tr>
          </thead>
          <tbody className="tabular-nums">
            {rows.length === 0 && <tr><td colSpan={12} className="p-8 text-center text-zinc-400">No active profit-share configurations{canAdmin ? " yet — click New Configuration to add one." : "."}</td></tr>}
            {rows.map((r) => (
              <tr key={r.config_id} className="border-t border-zinc-100" data-testid={`ps-row-${r.centre.code}`}>
                <td className="p-3"><span className="font-mono text-xs">{r.centre.code}</span> · {r.centre.name}</td>
                <td className="p-3">{r.coordinator.name}</td>
                <td className="p-3 text-right">{fmtINR(r.income)}</td>
                <td className="p-3 text-right">{fmtINR(r.expenses)}</td>
                <td className="p-3 text-right">{fmtINR(r.assets)}</td>
                <td className={`p-3 text-right font-medium ${r.net_profit >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{fmtINR(r.net_profit)}</td>
                <td className="p-3 text-right">{r.share_pct}%</td>
                <td className="p-3 text-right font-medium">{fmtINR(r.coordinator_share)}</td>
                <td className="p-3 text-right text-zinc-600">{fmtINR(r.company_share)}</td>
                <td className="p-3 text-right">{fmtINR(r.total_paid)}</td>
                <td className={`p-3 text-right font-medium ${r.outstanding > 0 ? "text-amber-700" : "text-emerald-700"}`}>{fmtINR(r.outstanding)}</td>
                <td className="p-3 text-right">
                  {canPay && r.outstanding > 0 && (
                    <Button onClick={() => setPayingId(r.config_id)} size="sm" data-testid={`ps-pay-btn-${r.centre.code}`}
                      className="rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white text-xs h-8">Pay ₹{r.outstanding.toLocaleString("en-IN")}</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pay dialog */}
      {payingId && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="ps-pay-modal">
          <div className="bg-white max-w-lg w-full p-6">
            <div className="text-[10px] uppercase tracking-widest text-emerald-800">Process profit-share payment</div>
            <h3 className="font-heading text-xl mt-1 mb-4">{rows.find(r => r.config_id === payingId)?.coordinator?.name} — ₹{rows.find(r => r.config_id === payingId)?.outstanding?.toLocaleString("en-IN")}</h3>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Payment Mode</Label>
                <Select value={pf.payment_mode} onValueChange={(v) => setPf({ ...pf, payment_mode: v })}>
                  <SelectTrigger className="rounded-sm h-10 mt-1" data-testid="ps-pay-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>{["NEFT","RTGS","IMPS","UPI","Cheque","Bank Transfer"].map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Payment Date</Label>
                <Input type="date" value={pf.payment_date} onChange={(e) => setPf({ ...pf, payment_date: e.target.value })} className="rounded-sm h-10 mt-1" />
              </div>
              <div className="col-span-2"><Label>UTR / Reference</Label>
                <Input value={pf.utr} onChange={(e) => setPf({ ...pf, utr: e.target.value })} className="rounded-sm h-10 mt-1" data-testid="ps-pay-utr" />
              </div>
              <div className="col-span-2"><Label>Remarks</Label>
                <Input value={pf.remarks} onChange={(e) => setPf({ ...pf, remarks: e.target.value })} className="rounded-sm h-10 mt-1" />
              </div>
              <div className="col-span-2">
                <Label className="text-rose-600">Payment Acknowledgement (mandatory)</Label>
                <label className="mt-1 border-2 border-dashed border-emerald-300 p-4 flex flex-col items-center gap-1 cursor-pointer hover:bg-emerald-50">
                  <Paperclip size={16} />
                  <div className="text-sm">{pf.ack_filename || "Click to upload proof (PDF / JPG / PNG)"}</div>
                  <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png"
                    data-testid="ps-pay-ack-input"
                    onChange={(e) => e.target.files?.[0] && uploadAck(e.target.files[0])} />
                </label>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setPayingId(null)}>Cancel</Button>
              <Button onClick={() => pay(rows.find(r => r.config_id === payingId))}
                disabled={!pf.ack_file_id}
                data-testid="ps-pay-confirm"
                className="bg-emerald-700 hover:bg-emerald-800 text-white gap-2">
                <Check size={14} weight="bold" /> Confirm Payment
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Ledger */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Payment Ledger</div>
        <div className="bg-white border border-zinc-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="text-left p-3">Date</th>
                <th className="text-left p-3">Centre</th>
                <th className="text-left p-3">Mode</th>
                <th className="text-right p-3">Amount</th>
                <th className="text-left p-3">UTR</th>
                <th className="text-left p-3">Ack</th>
                <th className="text-left p-3">Paid By</th>
              </tr>
            </thead>
            <tbody className="tabular-nums">
              {ledger.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-zinc-400">No payment records yet.</td></tr>}
              {ledger.map((l) => (
                <tr key={l.id} className="border-t border-zinc-100" data-testid={`ps-ledger-${l.id}`}>
                  <td className="p-3 text-xs">{l.payment_date}</td>
                  <td className="p-3 text-xs font-mono">{l.centre_id.slice(0,8)}</td>
                  <td className="p-3 text-xs">{l.payment_mode}</td>
                  <td className="p-3 text-right font-medium text-emerald-700">{fmtINR(l.amount)}</td>
                  <td className="p-3 text-xs font-mono">{l.utr || "—"}</td>
                  <td className="p-3 text-xs">
                    {l.ack_file_id
                      ? <a target="_blank" rel="noopener noreferrer" href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${l.ack_file_id}/download`} className="text-zinc-700 hover:text-black underline inline-flex items-center gap-1"><Paperclip size={10} /> View</a>
                      : "—"}
                  </td>
                  <td className="p-3 text-xs text-zinc-600">{l.paid_by_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
